// A refused-for-credits reply: the SHELL owns the sentence, never the server's.
//
// The server writes a failed turn as an ordinary assistant message
// (ConversationOrchestratorService.failWorkspaceLoop). For the three credit
// codes its prose is English, names a page the product does not call that
// ("Credits & Usage"), tells the person that "a workspace admin can top up"
// when they ARE the admin, offers no button, and — when a tool ran first — it
// opens with "Before this error, the following actions completed successfully:
// Done: read_entities completed successfully..". All four personas of the
// round-1 onboarding audit met exactly that text as the first screen of a new
// workspace (needed 190 to 222, available 100).
//
// ChatMessage (packages/shared) renders `s.text` for these codes — it does not
// rewrite them the way it rewrites every other error — and it is not ours to
// edit. So the fix lives where the row is BUILT, in the workspace state layer:
// the transport's final patch (a live turn, a recovered turn, a resumed turn),
// sessions.js (a history reload, a thread switch) and the add-app reader each
// pass the row through `creditsRefusalRow` / `creditsRefusalRows`. The server's
// text never reaches a bubble; its numbers do, and they travel on the row's
// metadata (`creditsNeeded` / `creditsAvailable`) for the persistent banner.
//
// REGISTERED, NOT IMPORTED. These are functions of the shell's registry (the
// barrel, functions/index.js), reached as `el.call('creditsRefusalRow', …)`:
// the state-layer modules import nothing from here, so the dependency is one
// registry name, not a sibling import. Every export is SELF-CONTAINED (the
// FA204 convention of functions/creditGuard.js) — no module-scope helper or
// constant is read from inside one; the siblings are reached through
// `this.call`, the receiver `el.call` hands every function.
//
// The copy is reached through the caller's `say(key)` (the live shell
// element's polyglot), and the role through `isAdmin` — `creditsRefusalRow`
// supplies both from the element it runs on.

// The row is a credit refusal: the server's own boolean, or one of its three
// normalized codes. Same test as functions/creditGuard.js
// notifyCreditsExhaustedFromMessage, so the text and the notification never
// disagree about which rows they treat.
export const isCreditsError = function isCreditsError(metadata) {
  if (!metadata || metadata.kind !== 'error') return false
  return (
    metadata.creditsExhausted === true ||
    ['INSUFFICIENT_CREDITS', 'SPEND_CAP_EXCEEDED', 'ALLOCATION_EXCEEDED'].includes(
      String(metadata.errorCode || '')
    )
  )
}

// → { needed, available } | null. The structured fields first
// (`metadata.creditsNeeded` / `creditsAvailable`, which the server adds), then
// the server's own wording until it does: "Insufficient credits: needed 44,
// available 39" in `metadata.errorMessage`, repeated in the answer prose as
// "(needed 222, available 100)".
export const creditsShortfall = function creditsShortfall(metadata, text) {
  const count = (value) => {
    if (value === null || value === undefined || value === '' || typeof value === 'boolean') {
      return null
    }
    const n = Number(value)
    return Number.isFinite(n) && n >= 0 ? n : null
  }
  const m = metadata || {}
  const needed = count(m.creditsNeeded)
  const available = count(m.creditsAvailable)
  if (needed !== null && available !== null) return { needed, available }
  const wording = /needed\s+(\d+(?:\.\d+)?)\s*,\s*available\s+(\d+(?:\.\d+)?)/i
  for (const source of [m.errorMessage, text]) {
    const hit = wording.exec(String(source || ''))
    if (hit) return { needed: Number(hit[1]), available: Number(hit[2]) }
  }
  return null
}

// The sentence: what happened, then the way out for THIS person. `say` answers
// a bundle key; `isAdmin` is a boolean or a function (read only when a credit
// refusal is actually being worded).
//
// The admin's way out is a LINK in the reply — the bubble renders markdown, and
// a markdown link is the shell's own `Link`, which the router takes (ChatMessage
// itself is the shared chat's and has no button slot for a reply). The words of
// the way out ARE the link ("Add credits to keep going." — the first two words
// open the credit page), so the sentence stays one sentence in every language. A
// member has no page to go to: they are told who to ask, and the credit pill
// carries the "Ask an admin" action.
//
// 'limit' = the balance is not the problem: a configured spend cap or the
// workspace's share of an organization pool. Neither has a "needs N, has M".
export const creditsErrorText = function creditsErrorText(metadata, text, opts) {
  const { say, isAdmin } = opts || {}
  const t = typeof say === 'function' ? say : (key) => key
  const code = String((metadata && metadata.errorCode) || '')
  const admin = typeof isAdmin === 'function' ? !!isAdmin() : !!isAdmin
  const limit = code === 'SPEND_CAP_EXCEEDED' || code === 'ALLOCATION_EXCEEDED'
  const credits = (n) => Number(n).toLocaleString('en-US', { maximumFractionDigits: 2 })
  let lead
  if (code === 'SPEND_CAP_EXCEEDED') lead = t('workspace.credits.chat.spendCap')
  else if (code === 'ALLOCATION_EXCEEDED') lead = t('workspace.credits.chat.allocation')
  else {
    const gap = this.call('creditsShortfall', metadata, text)
    lead = gap
      ? String(t('workspace.credits.chat.short'))
          .replace('{needed}', () => credits(gap.needed))
          .replace('{available}', () => credits(gap.available))
      : t('workspace.credits.chat.notEnough')
  }
  const link = (key) => `[${t(key)}](/admin/usage)`
  const way = admin
    ? String(
        t(limit ? 'workspace.credits.chat.limitAdmin' : 'workspace.credits.chat.balanceAdmin')
      ).replace('{link}', () =>
        link(limit ? 'workspace.credits.chat.limitLink' : 'workspace.credits.chat.addLink')
      )
    : t(limit ? 'workspace.credits.chat.limitMember' : 'workspace.credits.chat.balanceMember')
  return `${lead} ${way}`
}

// → { text, metadata }. A row that is not a credit refusal comes back as it
// went in (same references). A balance refusal also gets its numbers written
// onto the metadata, so the banner and the notification read ONE place
// whether the server sent them structured or only in its prose.
export const applyCreditsError = function applyCreditsError(text, metadata, opts) {
  if (!this.call('isCreditsError', metadata)) return { text, metadata }
  const code = String((metadata && metadata.errorCode) || '')
  const limit = code === 'SPEND_CAP_EXCEEDED' || code === 'ALLOCATION_EXCEEDED'
  const gap = limit ? null : this.call('creditsShortfall', metadata, text)
  return {
    text: this.call('creditsErrorText', metadata, text, opts),
    metadata: gap
      ? { ...metadata, creditsNeeded: gap.needed, creditsAvailable: gap.available }
      : metadata
  }
}

// THE STEP the state layer calls, per message: → { text, metadata } for a
// credit refusal (the shell's sentence in the session language, the way out for
// the viewer's role, the figures on the metadata), or null for any other row —
// the caller keeps what it has. `this` is the shell element: the copy through
// its polyglot, the role through `currentUserIsAdmin` (read only when a credit
// refusal is being worded; an unreadable role is a member's — the one line that
// offers no action they cannot take).
export const creditsRefusalRow = function creditsRefusalRow(text, metadata, root) {
  if (!this.call('isCreditsError', metadata)) return null
  const el = this
  return this.call('applyCreditsError', text, metadata, {
    say: (key) => {
      try {
        return String(el.call('polyglot', key))
      } catch (_) {
        return String(key)
      }
    },
    isAdmin: () => {
      try {
        return !!el.call('currentUserIsAdmin', root)
      } catch (_) {
        return false
      }
    }
  })
}

// THE STEP over a list of chat rows ({ text, metadata, … }): every credit
// refusal reworded, every other row the same reference. Never throws on a
// malformed list — a bare read gets what it gave.
export const creditsRefusalRows = function creditsRefusalRows(rows, root) {
  const list = Array.isArray(rows) ? rows : []
  return list.map((row) => {
    if (!row || !row.metadata || row.role === 'user') return row
    const refused = this.call('creditsRefusalRow', row.text, row.metadata, root)
    return refused ? { ...row, text: refused.text, metadata: refused.metadata } : row
  })
}
